import React, { useMemo, useState, useCallback } from 'react'
import {
  Box,
  Text,
  IcoMoon,
  Loader,
  ArrowDownIcon,
  AssignmentIcon,
  PrinterIcon,
  QuestionIcon,
  ShowIfDesktop,
} from '@usap/uicomponents'
import { outsideClickHandler } from '@usap/core'
import IconHeaderCard from '../IconHeaderCard'
import InvoiceWmo from '../InvoiceWmo'
import Orders from './Orders'
import { BoxWithPointer } from '../../common/StyledCSS'
import commaSeparetedValue from '../../../helpers/commaSeparetedValue'
import CovidMessage from '../../common/CovidMessage'
import ReturnDetails from './ReturnDetails/ReturnDetails'
import TempBreadcrumb from './ReturnDetails/TempBreadcrumb'
import { getButtonType, setUrlReturnTypeParam } from '../OrderHistory/components/buttons/manageButtonType'
import useGetLifetimeReplacementFlag from '../RMA/hooks/useGetLifetimeReplacementFlag'
import MyAccountOrderDetails from '../../Skeletons/MyAccountOrderDetails.skeleton'
import useWmoEnabled from '../../../../../carparts/src/shared/hooks/useWmoEnabled'
import OrderDetailsWMO from './OrderDetailsWMO.js'
import { useProductProtectionSubtext } from '../../../../../carparts/src/shared/components/ProductProtectionPlan/useProductProtectionPlan'

import {
  Container,
  SubHeading,
  OrderSummaryMobile,
  OrderSummaryMobileBox,
  OrderInformationBox,
  InformationBox,
  InformationTitleBox,
  InformationBodyBox,
  OrdersContainer,
  ToolTip,
  ToolTipContent,
  ToolTipArrow,
  SubTotalTipIcon,
  ToolTipContainer,
} from './OrderDetails.css'

export default function OrderDetails({
  isDesktop,
  config,
  orderDetails,
  orderInvoice,
  handlePrintInvoice,
  print,
  returnReasonUtils,
  isLoading,
  Link,
  creditStatus,
  returnRequestStatus,
  isPrintLoading,
  navigateToRMA,
  covidCookie,
  formatBrand,
  OpenChatHandler,
  coreConfig,
  isCustomerTrackOrderRoute,
  history,
  GTM,
  productProtectionPlan,
  invoiceLoading,
  pushNotificationData,
  recommendationsComponent,
  unfoldedShippingConfig,
}) {
  const currentUrl = typeof window === 'object' && window.location.href.split('/')
  const isTrackOrder = currentUrl && currentUrl[currentUrl.length - 1].includes('trackorder')
  const isLifetimeReplacement = useGetLifetimeReplacementFlag()
  const returnType = isLifetimeReplacement ? getButtonType() : ''
  const returnTypeParam = setUrlReturnTypeParam(returnType)
  const urlReturnTypeParam = returnTypeParam ? `?${returnTypeParam}` : ''

  const links = useMemo(() => [
    { text: 'My Account', value: '/myaccount' },
    {
      text: isTrackOrder
        ? 'Track Order'
        : 'Order History',
      value: isTrackOrder
        ? '/myaccount/trackorder'
        : `/myaccount/orderhistory${urlReturnTypeParam}`,
    },
    { text: 'Order Details', value: '' },
  ], [isTrackOrder, urlReturnTypeParam])

  const shipping = orderDetails && orderDetails.shipping
  const shipAddress = (shipping && shipping.address) || {}
  const billing = (orderDetails && orderDetails.billing) || {}
  const orderSummary = (orderDetails && orderDetails.orderSummary) || {}
  const items = (orderDetails && orderDetails.items) || []
  const [displayReturnDetails, setDisplayReturnDetails] = useState(false)
  const [returnDetailsData, setReturnDetailsData] = useState({})
  // eslint-disable-next-line no-undef
  const showMessage = __BROWSER__ && window.getCookieRegex(covidCookie) === '1'
  const [showToolTip, setShowToolTip] = useState(false)
  const onInvoicePrint = useCallback(() => {
    handlePrintInvoice()
  }, [handlePrintInvoice])

  const onToolTip = useCallback(e => {
    e.stopPropagation()
    outsideClickHandler(
      showToolTip,
      ['subtotaltooltipicon'],
      () => {
        setShowToolTip(false)
      },
    )
    setShowToolTip(!showToolTip)
  }, [showToolTip])

  const isWmoEnabled = useWmoEnabled()
  const isEppSubtext = useProductProtectionSubtext()

  if (isWmoEnabled)
    return <OrderDetailsWMO
      isDesktop={isDesktop}
      config={config}
      Link={Link}
      orderDetails={orderDetails}
      returnReasonUtils={returnReasonUtils}
      orderInvoice={orderInvoice}
      handlePrintInvoice={handlePrintInvoice}
      invoiceLoading={invoiceLoading}
      print={print}
      creditStatus={creditStatus}
      returnRequestStatus={returnRequestStatus}
      isLoading={isLoading}
      isPrintLoading={isPrintLoading}
      navigateToRMA={navigateToRMA}
      covidCookie={covidCookie}
      formatBrand={formatBrand}
      OpenChatHandler={OpenChatHandler}
      coreConfig={coreConfig}
      history={history}
      GTM={GTM}
      productProtectionPlan={productProtectionPlan}
      isEppSubtext={isEppSubtext}
      pushNotificationData={pushNotificationData}
      recommendationsComponent={recommendationsComponent}
      unfoldedShippingConfig={unfoldedShippingConfig}
    />

  if (isLoading) {
    return (
      <>
        <MyAccountOrderDetails />
        <Loader />
      </>
    )
  }

  if (!orderDetails || !Object.keys(orderDetails).length) return ''

  if (displayReturnDetails) {
    return (
      <ReturnDetails
        Link={Link}
        isDesktop={isDesktop}
        creditStatus={creditStatus}
        returnReasonUtils={returnReasonUtils}
        returnDetailsData={returnDetailsData}
        returnRequestStatus={returnRequestStatus}
        setReturnDetailsData={setReturnDetailsData}
        setDisplayReturnDetails={setDisplayReturnDetails}
        OpenChatHandler={OpenChatHandler}
        isCustomerTrackOrderRoute={isCustomerTrackOrderRoute}
      />
    )
  }

  return (
    <>
      {!isCustomerTrackOrderRoute && <TempBreadcrumb links={links} Link={Link} />}
      {isPrintLoading && <Loader />}
      {print && orderInvoice && <InvoiceWmo orderDetails={orderInvoice} pageType="orderDetails" />}
      <Container>
        <IconHeaderCard
          order={1}
          icon={AssignmentIcon}
          iconSize="30px"
          boxSize="58px"
          heading="Order Details"
          dashboard={isCustomerTrackOrderRoute}
          subHeading={(
            <SubHeading direction="row" gap="xsmall">
              <Text size="xsmall" color="dark-2" weight="bold">
                Order Number:
              </Text>
              <Text size="xsmall" color="greyText">
                {orderDetails.merchantOrderId}
              </Text>
              <Text size="xsmall" color="dark-2">
                |
              </Text>
              <Text size="xsmall" color="dark-2" weight="bold">
                Order Date:
              </Text>
              <Text size="xsmall" color="greyText">
                {orderDetails.orderDate}
              </Text>
            </SubHeading>
          )}
        />
        {showMessage && <CovidMessage borderColor="#4A90E2" />}
        <OrderSummaryMobile order={2}>
          <OrderSummaryMobileBox>
            <Box gap="small">
              <Box direction="row" justify="between">
                <Text size="small" color="dark-2">
                  Order Date:
                </Text>
                <Text size="small" color="dark-2">
                  {orderDetails.orderDate}
                </Text>
              </Box>

              <Box direction="row" justify="between">
                <Text size="small" color="dark-2">
                  Order #:
                </Text>
                <Text size="small" color="dark-2">
                  {orderDetails.merchantOrderId}
                </Text>
              </Box>
              <Box direction="row" justify="between">
                <Text size="medium" color="dark-2" weight="bold">
                  Total:
                </Text>
                <Text size="medium" color="brand" weight="bold">
                  {`$${orderSummary.total}`}
                </Text>
              </Box>
              <BoxWithPointer
                direction="row"
                gap="xsmall"
                margin={{ top: 'small' }}
                onClick={onInvoicePrint}
              >
                <IcoMoon
                  icon={PrinterIcon}
                  color="brand"
                  size="20px"
                  viewBoxProp="0 0 438.533 438.533"
                />
                <Text alignSelf="center" size="small" color="brand">
                  Print Invoice
                </Text>
              </BoxWithPointer>
            </Box>
          </OrderSummaryMobileBox>
        </OrderSummaryMobile>

        <OrderInformationBox>
          <InformationBox order={1} orderMobile={3}>
            <InformationTitleBox>
              <Text color="dark-2" size="small" weight={700}>
                SHIPPING INFORMATION
              </Text>
            </InformationTitleBox>
            <InformationBodyBox>
              <Box pad={{ vertical: 'xsmall' }}>
                <Text color="dark-2" size="medium" weight={700}>
                  {shipping.name}
                </Text>
                <Text color="dark-2" size="small">
                  {shipAddress.streetAddress}
                </Text>
                {shipAddress.suburbAddress && (
                  <Text color="dark-2" size="small">
                    {shipAddress.suburbAddress}
                  </Text>
                )}
                <Text color="dark-2" size="small">
                  {`${shipAddress.city} ${shipAddress.state
                    }, ${shipAddress.postcode}`}
                </Text>
                <Text color="dark-2" size="small">
                  {shipAddress.country}
                </Text>
                <Text color="dark-2" size="small">
                  {shipping.telephone}
                </Text>
                <ShowIfDesktop>
                  <BoxWithPointer
                    direction="row"
                    gap="xsmall"
                    margin={{ top: 'small' }}
                    onClick={onInvoicePrint}
                  >
                    <IcoMoon
                      icon={PrinterIcon}
                      color="brand"
                      size="20px"
                      viewBoxProp="0 0 438.533 438.533"
                    />
                    <Text alignSelf="center" size="small" color="brand">
                      Print Invoice
                    </Text>
                  </BoxWithPointer>
                </ShowIfDesktop>
              </Box>
            </InformationBodyBox>
          </InformationBox>

          <InformationBox order={2}>
            <InformationTitleBox>
              <Text color="dark-2" size="small" weight={700}>
                PAYMENT INFORMATION
              </Text>
            </InformationTitleBox>
            <InformationBodyBox>
              <Box pad={{ vertical: 'xsmall' }}>
                <Text color="dark-2" size="xsmall" weight={700}>
                  Payment Method
                </Text>
                <Text color="dark-2" size="small">
                  {billing.paymentMethod}
                </Text>
              </Box>
              <Box pad={{ vertical: 'xsmall' }}>
                <Text color="dark-2" size="xsmall" weight={700}>
                  Billing Address
                </Text>
                <Text color="dark-2" size="small">
                  {billing.address.streetAddress}
                </Text>
                <Text color="dark-2" size="small">
                  {billing.address.suburbAddress}
                </Text>
                <Text color="dark-2" size="small">
                  {`${billing.address.city} ${billing.address.state}, ${billing.address.postcode
                    }`}
                </Text>
                <Text color="dark-2" size="small">
                  {`Phone number ${billing.telephone}`}
                </Text>
              </Box>
            </InformationBodyBox>
          </InformationBox>

          <InformationBox
            order={3}
            orderMobile={1}
            withBorder
          >
            <InformationTitleBox>
              <Text color="dark-2" size="small" weight={700}>
                Order Summary
              </Text>
            </InformationTitleBox>

            <InformationBodyBox>
              <Box gap="small">
                <ToolTipContainer>
                  {showToolTip && (
                    <ToolTip id="tooltiptext">
                      <ToolTipContent
                        dangerouslySetInnerHTML={{
                          __html: `<b>Subtotal</b> is the sum of all item totals. 
                        Each item total is comprised of <b>Unit Price</b>, 
                        <b>Protection Plan</b>, and <b>Core Charge</b> (where applicable), 
                        multiplied by <b>Quantity</b>.`,
                        }}
                      />
                      <ToolTipArrow>
                        <IcoMoon icon={ArrowDownIcon} color="white" size="40px" />
                      </ToolTipArrow>
                    </ToolTip>
                  )}
                  <Text size="small" color="dark-2">
                    Subtotal:
                  </Text>
                  <SubTotalTipIcon
                    id="subtotaltooltipicon"
                  >
                    <IcoMoon
                      icon={QuestionIcon}
                      size="18px"
                      color="#b2b2b2"
                      onClick={onToolTip}
                    />
                  </SubTotalTipIcon>
                  <Text size="small" color="dark-2">
                    {`$${commaSeparetedValue(orderSummary.subtotal)}`}
                  </Text>
                </ToolTipContainer>

                <Box direction="row" justify="between">
                  <Text size="small" color="dark-2">
                    Shipping:
                  </Text>
                  <Text size="small" color="dark-2">
                    {`$${commaSeparetedValue(orderSummary.totalShipping)}`}
                  </Text>
                </Box>

                <Box direction="row" justify="between">
                  <Text size="small" color="dark-2">
                    Handling:
                  </Text>
                  <Text size="small" color="dark-2">
                    {`$${commaSeparetedValue(orderSummary.totalHandling)}`}
                  </Text>
                </Box>

                <Box direction="row" justify="between">
                  <Text size="small" color="dark-2">
                    Tax:
                  </Text>
                  <Text size="small" color="dark-2">
                    {`$${commaSeparetedValue(orderSummary.totalTax)}`}
                  </Text>
                </Box>

                {orderSummary.totalDiscount &&
                  (orderSummary.totalDiscount !== '0.00' &&
                    orderSummary.totalDiscount !== '0') ? (
                  <Box direction="row" justify="between">
                    <Text size="small" color="dark-2">
                      Coupon:
                    </Text>
                    <Text size="small" color="dark-2">
                      {`- $${commaSeparetedValue(orderSummary.totalDiscount)}`}
                    </Text>
                  </Box>
                )
                  : ''}

                <Box direction="row" justify="between">
                  <Text size="medium" color="dark-2" weight="bold">
                    Order Total:
                  </Text>
                  <Text size="medium" color="brand" weight="bold">
                    {`$${commaSeparetedValue(orderSummary.total)}`}
                  </Text>
                </Box>
              </Box>
            </InformationBodyBox>
          </InformationBox>
        </OrderInformationBox>

        <OrdersContainer>
          <Orders
            isDesktop={isDesktop}
            config={config}
            orderDetails={orderDetails}
            setDisplayReturnDetails={setDisplayReturnDetails}
            setReturnDetailsData={setReturnDetailsData}
            creditStatus={creditStatus}
            returnRequestStatus={returnRequestStatus}
            items={items}
            orderStatus={orderDetails.orderStatus}
            navigateToRMA={navigateToRMA}
            orderId={orderDetails.orderId}
            formatBrand={formatBrand}
            OpenChatHandler={OpenChatHandler}
            coreConfig={coreConfig}
          />
        </OrdersContainer>
      </Container>
    </>
  )
}